<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <div class="card" v-if="!isLoading">
          <div class="card-header">
            <h4 class="card-title">{{ offerTitle }} ({{ offer.id }})</h4>
            <div v-if="offer.company" class="mb-2">
              <router-link
                :to="{
                  name: 'company',
                  params: { companyId: offer.company.id }
                }"
              >
                {{ offer.company.name_fr }}
              </router-link>
            </div>
            <div class="badge badge-primary">
              {{ feedName }}
            </div>
          </div>
          <div class="card-body">
            <div class="offer-description mb-4" v-html="offerDescription" />

            <!-- NOC AI Matching Info -->
            <div class="noc-info-section mb-4">
              <h5>NOC Classification</h5>
              <div v-if="offer.noc_number" class="noc-info-card">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <!-- NOC Number (Code) -->
                    <div class="noc-code mb-2">
                      <strong>NOC Code:</strong> {{ offer.noc_number }}
                      <span
                        v-if="offer.noc_manually_overridden"
                        class="badge badge-warning ml-2"
                      >Manually Overridden</span>
                      <span
                        v-else-if="offer.noc_ai_confidence"
                        class="badge badge-info ml-2"
                      >AI Matched</span>
                    </div>
                    <!-- Official NOC Class Title (from noc_numbers table) -->
                    <div v-if="offer.noc_number_relation" class="noc-class-title mb-2">
                      <strong>Class Title:</strong>
                      <div class="ml-3">
                        <div><small class="text-muted">EN:</small> {{ offer.noc_number_relation.name_en }}</div>
                        <div><small class="text-muted">FR:</small> {{ offer.noc_number_relation.name_fr }}</div>
                      </div>
                    </div>
                    <!-- Specific Job Title (from noc_job_titles table) -->
                    <div v-if="offer.noc_job_title" class="noc-job-title mb-2">
                      <strong>Job Title:</strong>
                      <div class="ml-3">
                        <div><small class="text-muted">EN:</small> {{ offer.noc_job_title.title_en }}</div>
                        <div><small class="text-muted">FR:</small> {{ offer.noc_job_title.title_fr }}</div>
                      </div>
                    </div>
                    <div v-else class="noc-job-title mb-2 text-warning">
                      <strong>Job Title:</strong> <em>Not assigned (confidence below threshold)</em>
                    </div>
                    <!-- AI Confidence -->
                    <div v-if="offer.noc_ai_confidence" class="noc-confidence mt-2">
                      <strong>AI Confidence:</strong>
                      <span :class="confidenceClass">{{ confidencePercent }}</span>
                    </div>
                    <div v-if="offer.noc_last_matched_at" class="noc-matched-at mt-1 text-muted">
                      <small>Last matched: {{ formatDate(offer.noc_last_matched_at) }}</small>
                    </div>
                  </div>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="rerunNocMatching"
                    :disabled="isRerunningNoc"
                  >
                    <BSpinner
                      v-if="isRerunningNoc"
                      class="spinner-border spinner-border-sm mr-1"
                      label="Spinning"
                    ></BSpinner>
                    Re-run AI Matching
                  </b-button>
                </div>
              </div>
              <div v-else class="noc-info-card text-muted">
                <div class="d-flex justify-content-between align-items-center">
                  <span>No NOC code assigned yet</span>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="rerunNocMatching"
                    :disabled="isRerunningNoc"
                  >
                    <BSpinner
                      v-if="isRerunningNoc"
                      class="spinner-border spinner-border-sm mr-1"
                      label="Spinning"
                    ></BSpinner>
                    Run AI Matching
                  </b-button>
                </div>
              </div>
            </div>

            <NocFormGroup v-model="selectedNocJobTitle" :disabled="isPosting" label="Manual NOC Override" />
            <b-form-group>
              <label>Categories</label>
              <multiselect
                v-if="offerAttributes.category"
                :allow-empty="false"
                :disabled="isPosting || offerAttributes.category.length <= 1"
                :custom-label="attributeLabel"
                track-by="id"
                v-model="selectedCategories"
                :options="offerAttributes.category"
                :max="3"
                multiple
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label>Specializations</label>
              <multiselect
                v-if="offerAttributes.specialization"
                :allow-empty="false"
                :disabled="
                  isPosting || offerAttributes.specialization.length <= 1
                "
                :custom-label="attributeLabel"
                track-by="id"
                v-model="selectedSpecializations"
                :options="offerAttributes.specialization"
                :max="3"
                multiple
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label>Experiences</label>
              <multiselect
                v-if="offerAttributes.experience"
                :allow-empty="false"
                :disabled="isPosting || offerAttributes.experience.length <= 1"
                :custom-label="attributeLabel"
                track-by="id"
                v-model="selectedExperiences"
                :options="offerAttributes.experience"
                :max="3"
                multiple
              ></multiselect>
            </b-form-group>
            <div>
              <router-link
                :to="{ name: 'pending-feed-offers' }"
                class="btn btn-secondary"
                >Back</router-link
              >
              <b-button
                variant="primary"
                @click.prevent="updateOffer"
                :disabled="isPosting"
                >Save</b-button
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <div class="card" v-if="!isLoading">
          <div class="card-header">
            <div class="d-flex justify-content-between">
              <h4 class="card-title">Publishing</h4>
              <b-button
                variant="primary"
                @click="handlePublishOnAllUnpublishedJobboards"
                :disabled="
                  disableButtons || unpublishedJobboardIds.length === 0
                "
                ><BSpinner
                  class="spinner-border spinner-border-sm mr-2"
                  v-if="isSpinning('publish', 'all')"
                  label="Spinning"
                ></BSpinner
                >Publish all</b-button
              >
            </div>
          </div>
          <div class="card-body">
            <b-table :items="offer.jobboards" :fields="jobboardFields">
              <template #cell(actions)="{item}">
                <span
                  v-if="
                    item.name === 'linkedinJobs' && !canPublishOnLinkedInJobs
                  "
                  >Missing LinkedIn Id</span
                >
                <span
                  v-else-if="
                    item.name === 'jobBankCanadaJobs' &&
                      !canPublishOnJobBankCanadaJobs
                  "
                  >Missing Noc</span
                >
                <b-button
                  v-else-if="item.pivot.status !== 'published'"
                  class="fa"
                  v-bind:class="{
                    'fa-check': !isSpinning('publish', item.id)
                  }"
                  size="sm"
                  :disabled="disableButtons"
                  variant="success"
                  @click.prevent="handlePublishOffer(item.id)"
                  ><BSpinner
                    class="spinner-border spinner-border-sm"
                    v-if="isSpinning('publish', item.id)"
                    label="Spinning"
                  ></BSpinner
                ></b-button>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BFormGroup, BButton, BTable, BSpinner } from "bootstrap-vue";
import Multiselect from "vue-multiselect";
import NocFormGroup from "../../components/Offers/NocFormGroup";
import actionButtons from "../../mixins/actionButtons";
export default {
  mixins: [actionButtons],
  components: {
    Multiselect,
    NocFormGroup,
    BFormGroup,
    BButton,
    BTable,
    BSpinner
  },
  props: {
    offerId: {
      required: true,
      type: [String, Number]
    }
  },
  data() {
    return {
      offer: {},
      offerAttributes: {},
      isLoading: false,
      isPosting: false,
      isRerunningNoc: false,
      selectedNocJobTitle: null,
      selectedCategories: [],
      selectedSpecializations: [],
      selectedExperiences: [],
      jobboardFields: [
        "label",
        { key: "pivot.status", label: "Status" },
        "actions"
      ]
    };
  },
  async created() {
    this.isLoading = true;
    await this.fetchOffer();
    this.setSelectedValues();
    await this.fetchOfferAttributes();
    this.isLoading = false;
  },
  computed: {
    offerTitle() {
      return this.offer.contents[this.offer.primary_language].title;
    },
    offerDescription() {
      return this.offer.contents[this.offer.primary_language].description;
    },
    feedName() {
      return this.offer.feed_in.legacy_client;
    },
    unpublishedJobboardIds() {
      return this.offer.jobboards
        .filter(jobboard => jobboard.pivot.status !== "published")
        .map(jobboard => jobboard.id);
    },
    canPublishOnLinkedInJobs() {
      return !!this.offer.company.linkedin_id;
    },
    canPublishOnJobBankCanadaJobs() {
      return !!this.offer.noc_job_title_id;
    },
    confidencePercent() {
      if (!this.offer.noc_ai_confidence) return "N/A";
      return `${(this.offer.noc_ai_confidence * 100).toFixed(1)}%`;
    },
    confidenceClass() {
      if (!this.offer.noc_ai_confidence) return "";
      const confidence = this.offer.noc_ai_confidence;
      if (confidence >= 0.8) return "text-success font-weight-bold";
      if (confidence >= 0.6) return "text-warning font-weight-bold";
      return "text-danger font-weight-bold";
    }
  },
  methods: {
    setSelectedValues() {
      this.selectedNocJobTitle = this.offer.noc_job_title;
      this.selectedCategories = this.offer.offer_attributes.filter(
        this.attributesByType("category")
      );
      this.selectedSpecializations = this.offer.offer_attributes.filter(
        this.attributesByType("specialization")
      );
      this.selectedExperiences = this.offer.offer_attributes.filter(
        this.attributesByType("experience")
      );
    },
    async fetchOffer() {
      await this.$http
        .get(`/enroll_admin/not-auto-publish-feed-offers/${this.offerId}`)
        .then(({ data }) => {
          this.offer = data;
        });
    },
    async updateOffer() {
      this.isPosting = true;

      const payload = {
        categories: this.selectedCategories.map(attribute => attribute.id),
        specializations: this.selectedSpecializations.map(
          attribute => attribute.id
        ),
        experiences: this.selectedExperiences.map(attribute => attribute.id),
        noc_job_title: this.selectedNocJobTitle
          ? this.selectedNocJobTitle.id
          : null
      };

      await this.$http
        .put(`/enroll_admin/offer/${this.offerId}`, payload)
        .then(async () => {
          await this.fetchOffer();
        })
        .catch(this.errorToast)
        .finally(() => (this.isPosting = false));
    },
    async fetchOfferAttributes() {
      await this.$http.get(`/offer-attributes`).then(({ data }) => {
        this.offerAttributes = data;
      });
    },
    publishOffer(payload) {
      return this.$http
        .post(`/enroll_admin/offer/${this.offer.id}/publish`, payload)
        .then(async () => {
          await this.fetchOffer();
        })
        .finally(() => {
          this.toggleButtonsState();
        });
    },
    handlePublishOnAllUnpublishedJobboards() {
      this.toggleButtonsState("publish", "all");
      return this.publishOffer({
        jobboards: this.unpublishedJobboardIds
      });
    },
    handlePublishOffer(jobboardId) {
      this.toggleButtonsState("publish", jobboardId);
      return this.publishOffer({ jobboards: [jobboardId] });
    },
    attributeLabel({ label, label_fr }) {
      return label_fr || label.fr;
    },
    attributesByType(type) {
      return attribute => {
        return attribute.type === type;
      };
    },
    errorToast(error) {
      Object.values(error.response.data).forEach(message => {
        this.$bvToast.toast(message, {
          variant: "danger"
        });
      });
    },
    async rerunNocMatching() {
      this.isRerunningNoc = true;
      await this.$http
        .post(`/enroll_admin/offer/${this.offerId}/rerun-noc-matching`)
        .then(() => {
          this.$bvToast.toast(
            "NOC matching job dispatched. Refresh in a few seconds to see the result.",
            { variant: "success" }
          );
        })
        .catch(this.errorToast)
        .finally(() => (this.isRerunningNoc = false));
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleString();
    }
  }
};
</script>
<style scoped>
.offer-description {
  max-height: 300px;
  overflow: auto;
  border: 1px solid;
  border-radius: 5px;
  padding: 10px;
}
.noc-info-section h5 {
  margin-bottom: 0.75rem;
}
.noc-info-card {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 15px;
}
</style>
